import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Button from '../components/elements/Button';
import RelatedPages from '../components/sections/partials/RelatedPages';
import { useLanguage } from '../contexts/LanguageContext';
import { seoContent } from '../seo/content';
import { caseStudyJsonLd } from '../seo/schema';
import useIsMobile from '../utils/useIsMobile';
import { localizedPath } from '../utils/localizedPath';

/**
 * One view for both production cases (Estudios Tributarios, Prosperia).
 *
 * Like ContentPage it owns no copy: it takes the route key and the case key,
 * reads `seoContent[lang].caseStudies[caseKey]` from src/seo/content.js
 * (`{ slug, title, description, problem, approach, result, faqs }`) and renders
 * it into the existing dark theme.
 *
 * Estudios Tributarios is the positioning case — it is the Moodle agent in
 * production — so it carries a prominent link to the Moodle page just under the
 * heading, where a visitor reads it before anything else.
 */

const UI = {
  en: {
    problem: 'The problem',
    approach: 'The approach',
    result: 'The result',
    faq: 'Frequently asked questions',
    email: 'Send an Email',
    caseLabel: 'Case study',
  },
  es: {
    problem: 'El problema',
    approach: 'El enfoque',
    result: 'El resultado',
    faq: 'Preguntas frecuentes',
    email: 'Enviar Email',
    caseLabel: 'Caso de éxito',
  },
};

/** Per-case chrome the copy file does not carry: the CTA and the WhatsApp opener. */
const CASE_UI = {
  estudiosTributarios: {
    en: {
      cta: 'Put a cited 24/7 tutor inside your own Moodle course',
      button: 'Start on WhatsApp',
      whatsapp: 'Hello, I saw the Estudios Tributarios case and want an AI tutor in my Moodle course.',
      moodleLead: 'This is the Moodle course agent, in production. Read how the',
      moodleLink: 'Moodle AI agent',
      moodleTail: 'works and what it takes to put one inside your own course.',
    },
    es: {
      cta: 'Pon un tutor citado 24/7 dentro de tu propio curso en Moodle',
      button: 'Empezar por WhatsApp',
      whatsapp:
        'Hola, vi el caso de Estudios Tributarios y quiero un tutor de IA en mi curso de Moodle.',
      moodleLead: 'Este es el agente de curso en Moodle, en producción. Lee cómo funciona el',
      moodleLink: 'agente de IA para Moodle',
      moodleTail: 'y qué hace falta para poner uno dentro de tu propio curso.',
    },
  },
  prosperia: {
    en: {
      cta: 'Want an agent like this answering your clients?',
      button: 'Start on WhatsApp',
      whatsapp: 'Hello, I saw the Prosperia case and want an AI agent for my clients.',
      moodleLead: 'Teaching rather than accounting? The same studio builds the',
      moodleLink: 'Moodle AI agent',
      moodleTail: 'that tutors students inside a course.',
    },
    es: {
      cta: '¿Quieres un agente así respondiendo a tus clientes?',
      button: 'Empezar por WhatsApp',
      whatsapp: 'Hola, vi el caso de Prosperia y quiero un agente de IA para mis clientes.',
      moodleLead: '¿Formación en lugar de contabilidad? El mismo estudio construye el',
      moodleLink: 'agente de IA para Moodle',
      moodleTail: 'que tutoriza alumnos dentro de un curso.',
    },
  },
};

const WHATSAPP_NUMBER = '19544708414';
const CONTACT_EMAIL = 'info@e-comienza.com';

const propTypes = {
  /** Route key from src/seo/routes.js: 'caseEstudios' or 'caseProsperia'. */
  routeKey: PropTypes.string.isRequired,
  /** Key into seoContent[lang].caseStudies. */
  caseKey: PropTypes.string.isRequired,
};

const CaseStudyPage = ({ routeKey, caseKey }) => {
  const { lang } = useLanguage();
  const isMobile = useIsMobile();

  const content = seoContent[lang] || seoContent.en;
  const study = content.caseStudies[caseKey];
  const t = UI[lang] || UI.en;
  const c = (CASE_UI[caseKey] || CASE_UI.prosperia)[lang] ||
    (CASE_UI[caseKey] || CASE_UI.prosperia).en;

  const waQuery = `phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(c.whatsapp)}`;
  const waUrl = isMobile
    ? `whatsapp://send?${waQuery}`
    : `https://web.whatsapp.com/send?${waQuery}`;

  // problem / approach / result are the three labelled sections of every case.
  const blocks = [
    { id: 'problem', heading: t.problem, body: study.problem },
    { id: 'approach', heading: t.approach, body: study.approach },
    { id: 'result', heading: t.result, body: study.result },
  ];

  return (
    <>
      <Seo
        title={study.title}
        description={study.description}
        routeKey={routeKey}
        jsonLd={caseStudyJsonLd(routeKey, study, lang)}
      />

      <article className="section content-page">
        <div className="container">
          <div className="section-inner">
            <header className="content-page-header">
              <span className="section-label">{t.caseLabel}</span>
              <h1 className="mt-0 mb-16">{study.title}</h1>
              <p className="content-page-intro mb-0">{study.description}</p>
            </header>

            {/* The positioning link. On the Estudios Tributarios page this is the
                point of the page, so it sits above the narrative. */}
            <aside className="case-positioning">
              <p className="mb-0">
                {c.moodleLead}{' '}
                <Link to={localizedPath('moodleAgent', lang)}>{c.moodleLink}</Link>{' '}
                {c.moodleTail}
              </p>
            </aside>

            {blocks.map((block) => (
              <section
                key={block.id}
                className="content-block"
                aria-labelledby={`${routeKey}-${block.id}`}
              >
                <h2 id={`${routeKey}-${block.id}`} className="mt-0 mb-16">
                  {block.heading}
                </h2>
                <p className="mb-0">{block.body}</p>
              </section>
            ))}

            <RelatedPages from={routeKey} />

            {/* Plain text in the DOM, exactly like the long-form pages: the FAQPage
                markup and what a reader sees are the same words. */}
            <section className="content-faq" aria-labelledby={`${routeKey}-faq`}>
              <h2 id={`${routeKey}-faq`} className="mt-0 mb-16">
                {t.faq}
              </h2>
              <div className="faq-list">
                {study.faqs.map((faq) => (
                  <div className="faq-item" key={faq.question}>
                    <h3 className="mt-0 mb-8">{faq.question}</h3>
                    <p className="mb-0">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </article>

      <section className="cta section cta-overhaul has-top-divider">
        <div className="container">
          <div className="cta-inner section-inner">
            <h2 className="mt-0 mb-0">{c.cta}</h2>
            <div className="content-cta-actions">
              <Button tag="a" color="primary" href={waUrl} target="_blank" rel="noopener noreferrer">
                {c.button}
              </Button>
              <a className="button button-ghost" href={`mailto:${CONTACT_EMAIL}`}>
                {t.email}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

CaseStudyPage.propTypes = propTypes;

export default CaseStudyPage;
